// Answer bank: every answer written in a draft, plus ones the user adds.
//
//   users/{uid}/applicationQuestions   the answers written in drafts (read only here)
//   users/{uid}/answerBank/{id}        BankDoc: an answer of their own, or the
//                                      bank's copy of a draft answer (from = its
//                                      question id) after an edit, a theme change
//                                      or a delete. Drafts never change from here.

import { toMs, type Draft, type DraftQuestion } from "./drafts";

export type Theme = "about" | "why" | "built" | "leadership" | "teamwork" | "challenge" | "other";

export const THEMES: { id: Theme; label: string }[] = [
  { id: "why", label: "Why this company" },
  { id: "built", label: "Something you built" },
  { id: "leadership", label: "Leadership" },
  { id: "teamwork", label: "Teamwork" },
  { id: "challenge", label: "Challenges" },
  { id: "about", label: "About you" },
  { id: "other", label: "Other" },
];

export const THEME_LABEL = Object.fromEntries(THEMES.map((t) => [t.id, t.label])) as Record<Theme, string>;

// First match wins, so the specific ones go before "why" and "built"
const THEME_RULES: [Theme, RegExp][] = [
  ["why", /^\s*why\b/],
  ["about", /\b(about yourself|tell us about you\b|introduce yourself|describe yourself|who are you|your background)/],
  ["challenge", /\b(fail|failure|mistake|challeng|difficult|obstacle|setback|overc[oa]me|struggl|went wrong)/],
  ["leadership", /\b(lead|led|leader|leadership|initiative|took charge|mentor)/],
  ["teamwork", /\b(team|collaborat|conflict|disagree|cowork|co-work)/],
  ["built", /\b(project|built|build|created?|technical|proud|shipped|side project|hackathon)/],
  ["why", /\b(why|interest(ed|s)? you|excites? you|draws? you|made you|motivat)/],
];

/** The theme a question asks about, from its words. */
export function themeOf(question: string): Theme {
  const q = question.toLowerCase();
  return THEME_RULES.find(([, rule]) => rule.test(q))?.[0] ?? "other";
}

export type BankDoc = {
  id: string;
  /** The draft question this copies; none for their own */
  from?: string;
  title?: string;
  answer?: string;
  theme?: Theme;
  /** A draft answer taken out of the bank */
  hidden?: boolean;
  createdAt?: any;
  updatedAt?: any;
};

export type BankUse = { draftId: string; company: string; role: string };

export type BankAnswer = {
  /** Stable: the bank doc's id for their own, `q:<question id>` for a draft answer */
  key: string;
  title: string;
  answer: string;
  theme: Theme;
  /** The drafts it was written for, newest first */
  uses: BankUse[];
  /** Last used or edited */
  updatedAt: number;
  /** The question it comes from; undefined for their own */
  from?: string;
  /** Its answerBank doc, if there is one */
  docId?: string;
};

const sameText = (text: string) => text.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * The bank: draft answers (one per distinct answer, however many drafts used it),
 * with the user's edits laid over them, and their own answers. Last used first.
 */
export function buildBank(drafts: Draft[], questions: DraftQuestion[], docs: BankDoc[]): BankAnswer[] {
  const draftById = new Map(drafts.map((d) => [d.id, d]));
  const copies = new Map(docs.filter((d) => d.from).map((d) => [d.from!, d]));

  const groups = new Map<string, DraftQuestion[]>();
  for (const q of questions) {
    if (!q.answer?.trim() || !draftById.has(q.applicationId)) continue;
    const key = sameText(q.answer);
    groups.set(key, [...(groups.get(key) || []), q]);
  }

  const fromDrafts: BankAnswer[] = [];
  groups.forEach((group) => {
    const when = (q: DraftQuestion) => {
      const draft = draftById.get(q.applicationId)!;
      return Math.max(toMs(draft.appliedAt), toMs(draft.createdAt), toMs((q as any).createdAt));
    };
    const newest = [...group].sort((a, b) => when(b) - when(a));
    // The first question written keeps the key, so edits stay attached as it's reused
    const first = [...group].sort((a, b) => (a.id < b.id ? -1 : 1))[0];
    const copy = copies.get(first.id);
    if (copy?.hidden) return;
    const seen = new Set<string>();
    const uses = newest
      .filter((q) => !seen.has(q.applicationId) && seen.add(q.applicationId))
      .map((q) => {
        const draft = draftById.get(q.applicationId)!;
        return { draftId: draft.id, company: draft.company, role: draft.role };
      });
    fromDrafts.push({
      key: `q:${first.id}`,
      from: first.id,
      docId: copy?.id,
      title: copy?.title || newest[0].question,
      answer: copy?.answer ?? newest[0].answer,
      theme: copy?.theme || themeOf(newest[0].question),
      uses,
      updatedAt: Math.max(when(newest[0]), toMs(copy?.updatedAt)),
    });
  });

  const own: BankAnswer[] = docs
    .filter((d) => !d.from && !d.hidden && (d.answer?.trim() || d.title?.trim()))
    .map((d) => ({
      key: d.id,
      docId: d.id,
      title: d.title?.trim() || "Untitled answer",
      answer: d.answer || "",
      theme: d.theme || themeOf(d.title || ""),
      uses: [],
      updatedAt: Math.max(toMs(d.updatedAt), toMs(d.createdAt)),
    }));

  return [...fromDrafts, ...own].sort((a, b) => b.updatedAt - a.updatedAt);
}

export function matchesAnswer(item: BankAnswer, term: string): boolean {
  const t = term.trim().toLowerCase();
  return (
    !t ||
    item.title.toLowerCase().includes(t) ||
    item.answer.toLowerCase().includes(t) ||
    item.uses.some((u) => u.company.toLowerCase().includes(t))
  );
}

/** "Used in 2 apps", or "Not used yet" for their own */
export const usedLabel = (item: BankAnswer) =>
  item.uses.length ? `Used in ${item.uses.length} app${item.uses.length === 1 ? "" : "s"}` : "Not used yet";
